import {Injectable} from '@angular/core';
import {Observable, catchError, throwError} from 'rxjs';

import {
  CacheExpirationEvent,
  CacheExpirationStrategyService,
} from '@services/cache-expiration-strategy.service';
import {APIError} from '@utils/api-errors';
import {EventAPIService, SendEventInput} from '../event-api.service';
import {EntryStore} from '@stores/entry.store';
import {
  Action as AdvanceAction,
  AdvanceWithActionTransformer,
} from '@stores/entry-store-transformers/advance-with-action.transformer';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {ServiceDecorator} from './service-decorator';
import {isEntryChange} from '@utils/event-utils';

@Injectable()
export class AdvanceEntryStateAroundEventDecorator
  implements EventAPIService, ServiceDecorator<EventAPIService>
{
  private _service: EventAPIService;

  constructor(
    private entryStore: EntryStore,
    private advanceWithActionTransformer: AdvanceWithActionTransformer,
    private console: APIError.Console,
    private cacheExpirationStrategy: CacheExpirationStrategyService,
  ) {}

  setService(service: EventAPIService): void {
    this._service = service;
  }

  sendEvent(input: SendEventInput): Observable<string | void> {
    if (U.isEmpty(this._service)) {
      throw new Error(
        '[AdvanceEntryStateAroundEventDecorator]service not initialized',
      );
    }

    if (!isEntryChange(input.eventType)) {
      return this._service.sendEvent(input);
    }

    const payload = input.c as PL2.AnyPartialEntryWithId[];
    this.entryStore.modifyStateWithTransformer(
      {
        payload: payload,
        action: AdvanceAction.Awaiting,
        intent: input.eventType,
      },
      this.advanceWithActionTransformer,
    );

    return this._service.sendEvent(input).pipe(
      catchError((err) => {
        this.console.error(err);
        this.cacheExpirationStrategy.deepExpire(
          CacheExpirationEvent.Error,
          this.entryStore,
          payload,
        );
        const standardizedError = APIError.buildError(err);
        this.entryStore.modifyStateWithTransformer(
          {
            payload: payload,
            action: AdvanceAction.Failed,
            intent: input.eventType,
            error: standardizedError,
          },
          this.advanceWithActionTransformer,
        );
        return throwError(() => standardizedError);
      }),
    );
  }
}
